<template>
  <BaseLayout>
    <TimelineLoading :step="this.loadStep" :loading="isLoading">
      <div class="flex w-full sm:w-1/2 md:w-1/2 lg:w-1/3 xl:w-1/4 items-center justify-center">
        <OfferDetailContext step="DADOS_PESSOAIS" :offer="offer" :isAccordionOpen="true" :keepAccordionOpen="false" :colorSolid="false" :mobileOnly="true">
          <div class="flex flex-col py-0 sm:py-10 items-center w-full pb-10 sm:pb-0">
            <div class="flex flex-col items-start gap-2 sm:gap-10 w-full items-center">
              <div class="flex flex-col justify-start w-full items-center">
                <mdn-heading :level="2" size="sm">Vamos começar</mdn-heading>
                <mdn-text>Por favor, insira os seguintes dados para continuar:</mdn-text>
              </div>
              <UserForm @send="onSend" />

              <mdn-modal sm hideFooterStart v-if="isModalOpen" @close-modal="handleNoOp">
                <mdn-heading :level="1" slot="modal-header">Erro ao contratar plano</mdn-heading>
                <div slot="modal-body" class="mdn-Topic mdn-Topic--vertical mdn-Topic--icon">
                  <div class="mdn-Topic-symbol">x</div>
                  <p class="mdn-Topic-description">Você já possui o plano escolhido</p>
                </div>

                <div slot="modal-footer-end">
                  <Button id="modal-back" primary @click="handleNoOp">Voltar</Button>
                </div>
              </mdn-modal>
            </div>
          </div>
        </OfferDetailContext>
      </div>
    </TimelineLoading>
  </BaseLayout>
</template>

<script>
import UserForm from './UserForm'
import { serviceBuilder as userService } from '@/business/user/service'
import { serviceBuilder as offerService } from '@/business/offers/service'
import { singleOfferMixin, setOfferMixin } from '@/business/offers/mixins'
import { storeUserDataMixin } from '@/business/user/mixins'
import { manageTransactionIdMixin } from 'business/payment/mixins'
import { SMS_STORE_MSISDN_KEY, SHOULD_SAVE_MSISDN_AND_RECORD_ACTIONS, SMS_PEDIDO_VENDA_ID_KEY } from '@/common/constants'
import { storePedidoVendaId } from '@/business/televendas/pedido-venda-id'
import { useHttpServiceMixin } from '@/common/use-http'
import { removeQueryParamAndAddToStore, addToStore, isTestNumber } from '@/common/utils'
import store, { clearInitialValidation } from '@/business/store'
import { saleMixin } from '@/business/sale/mixins'
import { MdnHeading, MdnText, MdnModal, Button } from 'mondrian-vue'
import TimelineLoading from '../../components/TimelineLoading.vue'
import OfferDetailContext from '../../components/OfferDetailContext.vue'
import { onlyNumbers } from '@brazilian-utils/helper-only-numbers'
import { NotifyUserActionsEnum } from '../../business/http/dto/NotifyUserActionRequest'

export default {
  name: 'page-2-step',
  title: 'Informe seus dados',
  components: { TimelineLoading, UserForm, MdnHeading, MdnText, OfferDetailContext, MdnModal, Button },
  mixins: [
    singleOfferMixin,
    storeUserDataMixin,
    manageTransactionIdMixin,
    saleMixin,
    setOfferMixin,
    useHttpServiceMixin({ userService, offerService })
  ],
  data () {
    return {
      isProcessing: false,
      isModalOpen: false,
      loadStep: 'PLANO'
    }
  },
  computed: {
    isLoading () {
      return this.isOfferLoading || this.isProcessing
    }
  },
  onOfferError: {
    name: 'page-1-step'
  },
  methods: {
    async loadUtmData () {
      const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
      // eslint-disable-next-line camelcase
      const [utm_source, utm_medium, utm_name, utm_term, utm_content] = await Promise.all(keys.map(key => store.get(key)))
      return { utm_source, utm_medium, utm_name, utm_term, utm_content }
    },
    async onSend (rawData) {
      const data = { ...rawData, msisdn: onlyNumbers(rawData.msisdn), cpf: onlyNumbers(rawData.cpf) }
      this.isProcessing = true
      await this.storeUserData(data)
      try {
        const shouldSave = await store.get(SHOULD_SAVE_MSISDN_AND_RECORD_ACTIONS)
        if (shouldSave) {
          removeQueryParamAndAddToStore(store, SMS_STORE_MSISDN_KEY, data.msisdn)
        }

        if (this.validateInLastStep(data.msisdn) && !isTestNumber(data.msisdn)) {
          this.$router.push({ name: 'page-3-step' })
          return
        } else {
          await this.initialValidation(data.cpf, data.msisdn)
          this.confirmUserInitialValidation(true)
        }

        this.$tracker.log('clique:confirmar-dados', 'tentativa:sucesso')
      } catch (err) {
        this.$tracker.error(err)
        this.$tracker.log('clique:confirmar-dados', 'tentativa:erro')

        this.isProcessing = false
        this.$router.push({ name: 'page-error' })
      }
    },
    async handleNoOp () {
      this.$router.replace({ name: 'page-1-step' })
    }
  },
  async beforeMount () {
    const queryParams = this.$route.query || {}
    const pvidValue = queryParams[SMS_PEDIDO_VENDA_ID_KEY]
    if (pvidValue) {
      await clearInitialValidation()
      try {
        const decodedId = atob(pvidValue)
        if (decodedId.length === 36) storePedidoVendaId(decodedId)
      } catch (e) { /* silently ignore invalid base64 */ }
      this.$router.replace({ query: { ...queryParams, [SMS_PEDIDO_VENDA_ID_KEY]: undefined } })
    }

    const televendas = Buffer.from('' + this.$route.params.televendasdata, 'base64').toString('utf8').split(';')
    if (televendas.length === 3 || televendas.length === 4 || televendas.length === 5) {
      const [msisdn, , plan, televendasId, pedidoVendaIdFromPath] = televendas
      if (pedidoVendaIdFromPath && !pvidValue) {
        storePedidoVendaId(pedidoVendaIdFromPath)
      }
      const extra = { offerId: plan, origin: 'TELEVENDAS' }
      const idTelevendas = televendasId !== undefined ? televendasId : null
      this.$userService.notifyUserActionWithTelevendasId(msisdn, NotifyUserActionsEnum.CONTRATAR, extra, idTelevendas)
      addToStore(store, SHOULD_SAVE_MSISDN_AND_RECORD_ACTIONS, true)
      await this.setOffer(plan)
    }
  }
}
</script>
